import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Database } from '@/types/supabase';

import {
  OrganizationFull,
  OrganizationOverview,
  Team,
  Profile,
} from '@/types/custom';

/**
 * Get hierachy of organization, teams, and users.
 * Also returns users that don't belong to any team (and thus no org).
 */
export function useOrganizations(): [
  OrganizationOverview[] | null,
  Profile[],
  boolean,
] {
  const [organizations, setOrganizations] = useState<
    OrganizationOverview[] | null
  >(null);
  const [looseProfiles, setLooseProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        // https://supabase.com/docs/guides/database/joins-and-nesting
        // sort of the reverse of useUserProfile hook's query
        const { data: orgData, error: orgError } = (await supabase
          .from('organization')
          .select(
            `
          *,
          team:id (
            *,
            team_user_link!team_user_link_team_id_fkey (
              profiles:user_id (
                *
              )
            )
          )
          `,
          )) as unknown as { data: any | null; error: any };

        if (orgError) {
          console.error('Error fetching organizations:', orgError);
          setLoading(false);
          return;
        }

        const organizations: OrganizationOverview[] = orgData.map((org) => {
          // rename fields to match interface
          org.teams = org.team;
          delete org.team;

          org.teams = org.teams.map((t) => {
            t.users = t.team_user_link;
            delete t.team_user_link;

            t.users = t.users.map((u) => u.profiles);
            return t;
          });

          return org;
        });

        // https://supabase.com/docs/reference/javascript/not
        const profileIds = extractProfiles(orgData).map((p) => p.id);
        const excludeFilter = `(${profileIds.join(',')})`;

        let looseData;
        let looseError;
        if (profileIds.length > 0) {
          const result = await supabase
            .from('profiles')
            .select('*')
            .not('id', 'in', excludeFilter);
          looseData = result.data;
          looseError = result.error;
        } else {
          const result = await supabase.from('profiles').select('*');
          looseData = result.data;
          looseError = result.error;
        }

        if (looseError) {
          console.error('Error fetching profiles without team:', looseError);
          setLoading(false);
          return;
        }

        setOrganizations(organizations);
        setLooseProfiles(looseData);
      } catch (error) {
        console.error('Error in useOrganizations:', error);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, []);

  return [organizations, looseProfiles, loading];
}

export const extractProfiles = (
  organizations: OrganizationOverview[],
): Profile[] => {
  // Extract all profiles from all organizations and teams
  const allProfiles: (Profile & { team?: string; organisatie?: string })[] = [];

  organizations.forEach((org) => {
    org.teams.forEach((team) => {
      team.users.forEach((user) => {
        if (!user) {
          // TODO: understand why this happens
          console.warn(`user in org ${org.name} team ${team.name} is null`);
          return;
        }
        allProfiles.push(user);
      });
    });
  });
  //   console.log('organizations, ', organizations);
  //   console.log('allProfiles, ', allProfiles);

  // Remove duplicates based on profile.id using a Set
  const uniqueIds = new Set();
  const uniqueProfiles = allProfiles.filter((profile) => {
    if (!uniqueIds.has(profile.id)) {
      uniqueIds.add(profile.id);
      return true;
    }
    return false;
  });
  return uniqueProfiles;
};

export const countUsers = (org: OrganizationOverview) => {
  return org.teams.reduce((acc, team) => acc + team.users.length, 0);
};
